import IconInfo from "@common/components/icons/IconInfo";
import WarningCard from "@common/components/thirdParty/mantine/card/WarningCard";
import { Loader, Text, useMantineTheme } from "@mantine/core";
import { forwardRef, ReactElement, Ref, useImperativeHandle } from "react";
import { useAccount } from "@stores/session/hooks";
import useIsValidMigrationId from "@hooks/useIsValidMigrationId";
import { useRouter } from "next/router";
import { MigrationCardRefType } from "./types";
import { IWarningCardProps } from "@common/components/thirdParty/mantine/card/WarningCard/types";
import { persistUTMQueryParamsToString } from "@common/utils/router";
import Link from "next/link";

export default forwardRef(function MigrationCard(
  warningCardProps: IWarningCardProps,
  ref: Ref<MigrationCardRefType>,
): ReactElement | null {
  const router = useRouter();
  const { query, pathname } = router;

  const theme = useMantineTheme();

  const account = useAccount();

  const isLoggedIn = !!account;
  const isLoginPage = pathname.startsWith("/login");

  const { isLoading, error, migrationId, isValid } = useIsValidMigrationId();

  useImperativeHandle(ref, () => ({
    isLoading,
    error,
    migrationId,
    isValid,
  }));

  if (!migrationId) {
    return null;
  }

  if (isLoading) {
    return <Loader size="xs" />;
  }

  return (
    <WarningCard
      data-testid={`MigrationCard_${isLoginPage ? "login" : "checkout"}`}
      icon={<IconInfo color={theme.colors.yellow[3]} size={24} />}
      title={
        isLoggedIn ? (
          "Migration process"
        ) : isLoginPage ? (
          <>
            {/* need a real link here to get to a different vercel project */}
            <a
              href={persistUTMQueryParamsToString(router, "/checkout", {
                ...query,
                continue: 1,
              })}
            >
              Sign up
            </a>{" "}
            or log in to proceed with migration
          </>
        ) : (
          <>
            Sign up or{" "}
            <Link href={persistUTMQueryParamsToString(router, "/login", query)}>
              log in
            </Link>{" "}
            to proceed with migration
          </>
        )
      }
      {...warningCardProps}
    >
      {isValid ? (
        <Text fz="sm">
          {isLoggedIn || isLoginPage
            ? "Once your instance is created, you will be redirected to the migration page where you will see its progress."
            : "Sign up for a Metabase account to continue your cloud migration."}
        </Text>
      ) : (
        <Text fz="sm">
          The migration id is invalid but you can still create a new instance...
        </Text>
      )}
    </WarningCard>
  );
});
